<template>
  <div class="lightning-product-search" :class="{ 'is-inline': inlineResults }">
    <label :for="inputId">{{ label }}</label>
    <div class="product-search-field">
      <SearchIcon aria-hidden="true" />
      <input :id="inputId" ref="searchInput" v-model="query" type="search" role="combobox" aria-autocomplete="list" aria-haspopup="grid"
        :aria-expanded="showResults" :aria-controls="`${searchPrefix}-results`" :aria-activedescendant="showResults && results[activeIndex] ? `${searchPrefix}-row-${results[activeIndex].id}` : undefined"
        placeholder="Search by product name or SKU" aria-label="Search products to add by name or SKU" autocomplete="off" @focus="open = Boolean(query.trim())"
        @keydown.down.prevent="moveActive(1)" @keydown.up.prevent="moveActive(-1)" @keydown.enter.prevent="addActive" @keydown.tab="focusQuantity" @keydown.esc.prevent="open = false" />
      <Icon v-if="loading" name="reload" class="search-loading animate-spin" aria-hidden="true" />
    </div>
    <div v-if="showResults" class="product-search-popup" @keydown.esc.prevent="closeAndFocus">
      <div :id="`${searchPrefix}-results`" role="grid" aria-label="Product search results" class="search-results-scroller" :aria-busy="loading">
        <div v-for="(item, index) in results" :id="`${searchPrefix}-row-${item.id}`" :key="item.id" role="row" class="search-result" :class="{ 'is-active': activeIndex === index }" :aria-selected="activeIndex === index" @mouseenter="activeIndex = index">
          <div role="gridcell" class="search-result-info">
            <img :src="item.imageThumbnailUrl || '/images/no-image.png'" alt="" />
            <div class="min-w-0">
              <strong>{{ item.productName || item.name }}<template v-if="item.label"> - {{ item.label }}</template></strong>
              <div class="search-result-meta"><span>${{ price(item) }}</span><span aria-hidden="true">·</span><span>{{ item.sku }}</span><span aria-hidden="true">·</span><span>{{ item.brand }}<template v-if="item.product_type"> / {{ item.product_type }}</template></span></div>
            </div>
          </div>
          <div role="gridcell" class="search-result-actions">
            <template v-if="isInCart(item.id)"><span class="search-result-badge">{{ cartStateLabel }}</span></template>
            <template v-else-if="quantityLimit(item) > 0">
              <span v-if="stagedItem(item)" class="search-result-list-state"><span class="search-result-badge is-in-list">In your list ({{ stagedItem(item).qty }})</span></span>
              <label :for="`${searchPrefix}-qty-${item.id}`" class="sr-only">Quantity for {{ item.productName || item.name }}</label>
              <span class="search-quantity-label" aria-hidden="true">Qty</span>
              <input :id="`${searchPrefix}-qty-${item.id}`" :ref="el => setQuantityInput(item.id, el)" v-model.number="quantities[item.id]" type="number" inputmode="numeric" min="1" step="1" :max="Number.isFinite(quantityLimit(item)) ? quantityLimit(item) : undefined" :aria-describedby="!validQuantity(item) ? `${searchPrefix}-qty-hint-${item.id}` : undefined" :disabled="busy || submitting" @keydown.enter.prevent.stop="submit(item)" />
              <button type="button" class="search-add" :disabled="busy || submitting || !validQuantity(item)" @click="submit(item)">{{ stagedItem(item) ? 'Update' : 'Add' }}</button>
              <span v-if="!validQuantity(item)" :id="`${searchPrefix}-qty-hint-${item.id}`" class="search-quantity-hint">{{ quantities[item.id] > quantityLimit(item) ? `Maximum ${quantityLimit(item)}` : 'Use a whole quantity of 1 or more' }}</span>
            </template>
            <template v-else>
              <span class="search-result-badge is-out-of-stock">Out of stock</span>
              <button type="button" class="search-notify" :class="{ 'is-notified': notifyIds[item.id] }" :disabled="busy || notifyingIds.includes(item.id)" :aria-label="notifyIds[item.id] ? `Cancel stock notification for ${item.productName || item.name}` : undefined" @click="toggleNotification(item)"><Icon :name="notifyIds[item.id] ? 'checkmark' : 'bell-ring'" aria-hidden="true" />{{ notifyIds[item.id] ? 'We’ll notify you' : notifyingIds.includes(item.id) ? 'Saving…' : 'Notify me' }}</button>
            </template>
          </div>
        </div>
        <p v-if="!results.length" class="search-empty" role="status">{{ loading ? 'Searching products…' : error || 'No available products found. Try another name or SKU.' }}</p>
      </div>
      <p class="search-keyboard-hint">↑ ↓ to move · Tab to quantity · Enter to add and keep searching</p>
      <button type="button" class="search-paste-link" :disabled="busy" @click="open = false; $emit('paste')">Have a list? Paste SKUs to add everything at once</button>
    </div>
    <div v-if="message || (error && results.length)" class="search-feedback">
      <p v-if="message" class="search-message" role="status" :title="message">{{ message }}</p>
      <p v-if="error && results.length" class="search-error" role="alert" :title="error">{{ error }}</p>
    </div>
  </div>
</template>

<script>
import { nextTick } from "vue";
import { SearchIcon } from "@heroicons/vue/solid";
import { getOrderQuantityLimit } from "@/modules/CsvOrderUpload";

export default {
  name: "LightningProductSearch",
  components: { SearchIcon },
  props: {
    searchClient: { type: Object, required: true },
    isVisible: { type: Function, required: true },
    isInCart: { type: Function, required: true },
    addProduct: { type: Function, required: true },
    items: { type: Array, default: () => [] },
    prices: { type: Object, default: () => ({}) },
    notifyIds: { type: Object, default: () => ({}) },
    notifyingIds: { type: Array, default: () => [] },
    busy: { type: Boolean, default: false },
    inputId: { type: String, default: "searchlightning" },
    label: { type: String, default: "Search for products to add" },
    cartStateLabel: { type: String, default: "Already in order" },
    inlineResults: { type: Boolean, default: false },
    defaultQuantity: { type: Number, default: 1 },
  },
  emits: ["notify", "cancel-notify", "paste", "added"],
  data: () => ({ query: "", open: false, results: [], quantities: {}, quantityInputs: {}, activeIndex: 0, loading: false, submitting: false, error: "", message: "", messageItemId: null, messageTimer: null, searchTimer: null, requestVersion: 0 }),
  computed: {
    showResults() { return this.open && Boolean(this.query.trim()); },
    searchPrefix() { return this.inputId === "searchlightning" ? "lightning-search" : this.inputId; },
  },
  watch: {
    // The "Added …" status must not outlive the item it describes.
    items(rows) {
      if (this.messageItemId != null && !rows.some(row => row.id == this.messageItemId)) this.clearMessage();
    },
    query(value) {
      clearTimeout(this.searchTimer);
      const version = ++this.requestVersion;
      this.error = "";
      this.message = "";
      this.results = [];
      this.activeIndex = 0;
      this.open = Boolean(value.trim());
      this.loading = this.open;
      if (this.open) this.searchTimer = setTimeout(() => this.search(value.trim(), version), 250);
    },
  },
  mounted() {
    document.addEventListener("pointerdown", this.closeIfOutside, true);
    document.addEventListener("focusin", this.closeIfOutside);
  },
  beforeUnmount() {
    clearTimeout(this.searchTimer);
    clearTimeout(this.messageTimer);
    this.requestVersion++;
    document.removeEventListener("pointerdown", this.closeIfOutside, true);
    document.removeEventListener("focusin", this.closeIfOutside);
  },
  methods: {
    quantityLimit: getOrderQuantityLimit,
    stagedItem(item) { return this.items.find(row => row.id == item.id); },
    setQuantityInput(id, el) { if (el) this.quantityInputs[id] = el; else delete this.quantityInputs[id]; },
    price(item) { return Number(this.prices[item.id] ?? item.price ?? 0).toFixed(2); },
    toggleNotification(item) {
      if (this.busy || this.notifyingIds.includes(item.id)) return;
      this.$refs.searchInput.focus();
      this.$emit(this.notifyIds[item.id] ? "cancel-notify" : "notify", item);
    },
    validQuantity(item) { return Number.isSafeInteger(this.quantities[item.id]) && this.quantities[item.id] > 0 && this.quantities[item.id] <= this.quantityLimit(item); },
    async search(query, version) {
      try {
        const response = await this.searchClient.search([{ indexName: "variants_index", params: { query, hitsPerPage: 12 } }]);
        if (version !== this.requestVersion) return;
        this.results = (response.results[0]?.hits || []).filter(this.isVisible);
        this.results.forEach(item => { this.quantities[item.id] = Math.min(this.stagedItem(item)?.qty || this.defaultQuantity, this.quantityLimit(item)); });
      } catch (err) {
        if (version === this.requestVersion) this.error = "We couldn’t search right now. Try typing your search again.";
      } finally { if (version === this.requestVersion) this.loading = false; }
    },
    moveActive(direction) {
      if (!this.results.length) return;
      this.open = true;
      this.activeIndex = (this.activeIndex + direction + this.results.length) % this.results.length;
      nextTick(() => document.getElementById(`${this.searchPrefix}-row-${this.results[this.activeIndex]?.id}`)?.scrollIntoView({ block: "nearest" }));
    },
    focusQuantity(event) {
      if (event.shiftKey || !this.showResults) return;
      const item = this.results[this.activeIndex];
      const input = item && this.quantityInputs[item.id];
      if (input) { event.preventDefault(); input.focus(); input.select(); }
    },
    clearMessage() {
      clearTimeout(this.messageTimer);
      this.message = "";
      this.messageItemId = null;
    },
    addActive() { const item = this.results[this.activeIndex]; if (item) return this.submit(item); },
    async submit(item) {
      if (this.busy || this.submitting || this.isInCart(item.id) || !this.validQuantity(item)) return;
      this.error = "";
      this.message = "";
      this.submitting = true;
      const wasStaged = Boolean(this.stagedItem(item));
      try {
        const result = await this.addProduct({ item, qty: this.quantities[item.id] });
        if (!result.ok) { this.error = result.error; return; }
        this.quantities[item.id] = result.qty;
        this.messageItemId = item.id;
        clearTimeout(this.messageTimer);
        // Long enough for the role=status live region to be announced first.
        this.messageTimer = setTimeout(() => this.clearMessage(), 4000);
        this.message = `${wasStaged ? 'Updated' : 'Added'} ${item.productName || item.name} · ${result.qty} ${result.qty === 1 ? 'unit' : 'units'}${result.capped ? ' (quantity adjusted to available stock)' : ''}`;
        await nextTick();
        this.$refs.searchInput.focus();
        this.$refs.searchInput.select();
        this.$emit("added", result);
      } catch (err) { this.error = "We couldn’t add this product. Please try again."; }
      finally { this.submitting = false; }
    },
    closeIfOutside(event) {
      // Inline catalog results stay visible while interacting with the modal.
      if (this.inlineResults) return;
      // Clicking non-focusable content blurs the input without leaving the popup.
      if (event.type === "focusin" && (event.target === document.body || event.target === document.documentElement)) return;
      if (!this.$el.contains(event.target)) this.open = false;
    },
    closeAndFocus() { this.open = false; this.$refs.searchInput.focus(); this.open = false; },
    async openSearch(query = "") {
      this.query = query;
      this.open = Boolean(query.trim());
      await nextTick();
      this.$refs.searchInput.focus();
    },
  },
};
</script>

<style lang="scss" scoped>
.lightning-product-search { @apply relative min-w-0; > label { @apply block text-xs font-medium text-theme-secondary mb-2; } }
.product-search-field { @apply relative; > svg { @apply absolute left-3 top-3 w-5 h-5 text-theme-dark pointer-events-none; } > input { @apply block w-full rounded border border-gray-200 bg-gray-50 text-sm text-brand-primary pl-10 pr-9 py-3 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-indigodark; height: 44px; } .search-loading { @apply absolute right-3 top-3.5 w-4 h-4 text-theme-dark pointer-events-none; } }
.product-search-popup { @apply absolute inset-x-0 bg-white rounded-lg shadow-box border border-gray-200 overflow-hidden; bottom: calc(100% + 8px); z-index: 30; }
.search-results-scroller { @apply overflow-y-auto; max-height: min(50vh, 420px); @include CssScrollbar(); }
.search-result { @apply flex items-center justify-between gap-3 px-4 py-2.5 border-b border-gray-100; &.is-active { @apply bg-theme-purple-light; } }
.search-result-info { @apply flex items-center gap-3 min-w-0 flex-1; img { @apply w-10 h-10 object-contain rounded bg-white flex-shrink-0; } strong { @apply block text-sm font-medium text-brand-primary leading-5; overflow-wrap: anywhere; } span { @apply text-xs text-theme-dark leading-5; overflow-wrap: anywhere; } .search-result-meta { @apply flex flex-wrap items-center gap-x-1.5; } }
.search-result-actions { @apply flex flex-wrap items-center justify-end gap-2 flex-shrink-0; input { @apply w-14 rounded border border-gray-300 text-center text-sm text-brand-primary px-1 py-2 focus:outline-none focus:ring-2 focus:ring-brand-indigodark; height: 36px; appearance: textfield; } input::-webkit-inner-spin-button, input::-webkit-outer-spin-button { appearance: none; margin: 0; } }
.search-quantity-label { @apply text-xs text-theme-dark; }
.search-quantity-hint { @apply text-xs text-theme-dark text-right; flex-basis: 100%; }
.search-add, .search-notify { @apply inline-flex items-center justify-center rounded px-3 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-brand-indigodark focus:ring-offset-2; min-height: 36px; &:disabled { @apply opacity-50 cursor-not-allowed; } }
.search-add { @apply bg-brand-primary text-white hover:bg-opacity-90; min-width: 60px; }
.search-notify { @apply gap-1.5 bg-theme-red bg-opacity-10 text-red-700 hover:bg-opacity-20; svg { @apply w-3 h-3; } &.is-notified { @apply bg-theme-green-light text-theme-green-dark; } }
.search-result-list-state { @apply inline-flex; }
.search-result-badge { @apply inline-flex px-2 py-1 rounded text-xs text-theme-dark bg-gray-100; &.is-in-list { @apply bg-theme-purple-light text-brand-indigodarkest; } &.is-out-of-stock { @apply bg-theme-red bg-opacity-10 text-red-700; } }
.search-empty { @apply text-sm text-theme-dark px-4 py-4; }
.search-keyboard-hint { @apply px-4 py-2 text-xs text-theme-dark bg-gray-50; }
.search-paste-link { @apply block w-full text-left px-4 py-2.5 text-xs text-brand-indigodarkest font-medium hover:bg-theme-purple-light focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-indigodark; min-height: 36px; }
.search-feedback { @apply absolute inset-x-0; top: 100%; padding-top: 4px; }
.search-message, .search-error { @apply text-theme-dark leading-4 truncate; font-size: 11px; }
.search-error { @apply text-brand-primary; }
.is-inline { .product-search-popup { @apply relative shadow-none mt-2; bottom: auto; } .search-feedback { @apply static; } .search-message, .search-error { @apply whitespace-normal; } }
@media (max-width: 640px) {
  .search-result { @apply flex-wrap gap-2 px-3; }
  .search-result-info { flex-basis: 100%; }
  .search-result-actions { margin-left: 52px; max-width: calc(100% - 52px); }
  .search-result-list-state { flex-basis: 100%; }
  .search-quantity-label { @apply hidden; }
  .search-feedback { padding-top: 0; }
  .search-message, .search-error { font-size: 10px; line-height: 12px; }
  .search-results-scroller { max-height: 42vh; }
  .search-keyboard-hint { @apply hidden; }
}
</style>
